import { useState, useEffect } from "react";
import api from "../api";
import { AlertTriangle, TrendingUp, Wallet, ArrowDownCircle, ArrowUpCircle, Clock } from "lucide-react";

const fmt = (v) => "R$ " + Number(v || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtD = (d) => { if (!d) return "—"; return new Date(d).toLocaleDateString("pt-BR"); };
const dias = (d) => Math.round((new Date().setHours(0,0,0,0) - new Date(d).setHours(0,0,0,0)) / 86400000);
const STLBL = { rascunho:"Aguardando", enviado:"Enviado", aprovado:"Aprovado", recusado:"Financeiro", expirado:"Concluído" };

export default function Gestao({ currentUser }) {
  const [lancs, setLancs] = useState([]);
  const [orc, setOrc] = useState([]);
  const [leads, setLeads] = useState([]);
  const [saldoInicial, setSaldoInicial] = useState(0);
  const [load, setLoad] = useState(true);
  const [detalhe, setDetalhe] = useState(null);
  const _hoje = new Date();
  const [mesRef, setMesRef] = useState({ ano: _hoje.getFullYear(), mes: _hoje.getMonth() });
  const [modo, setModo] = useState("mes");

  useEffect(() => {
    Promise.all([
      api.get("/financeiro?de=2000-01-01&ate=2999-12-31&campoData=dataEmissao"),
      api.get("/financeiro/saldo"),
      api.get("/orcamentos"),
      api.get("/leads?limit=2000"),
    ]).then(([f, s, o, l]) => {
      setLancs(f.data.lancamentos || []);
      setSaldoInicial(s.data.saldoInicial || 0);
      setOrc(Array.isArray(o.data) ? o.data : (o.data.orcamentos || []));
      setLeads(l.data.leads || []);
    }).catch(() => {}).finally(() => setLoad(false));
  }, []);

  const hoje = new Date(); hoje.setHours(0,0,0,0);
  const noPeriodo = (dref) => { if (!dref) return false; const d = new Date(dref); if (modo === "ano") return d.getFullYear() === mesRef.ano; return d.getMonth() === mesRef.mes && d.getFullYear() === mesRef.ano; };
  const aberto = (l) => ["pendente","vencido"].includes(l.status);
  const venc = (l) => l.dataVencimento && new Date(l.dataVencimento) < hoje;
  const soma = (arr) => arr.reduce((a,l) => a + Number(l.valor||0), 0);
  const somaT = (arr) => arr.reduce((a,o) => a + Number(o.total||0), 0);

  const entradas = lancs.filter(l => l.tipo === "entrada");
  const saidas   = lancs.filter(l => l.tipo === "saida");
  const receberAberto = entradas.filter(l => aberto(l) && noPeriodo(l.dataVencimento || l.dataEmissao));
  const pagarAberto   = saidas.filter(l => aberto(l) && noPeriodo(l.dataVencimento || l.dataEmissao));
  const aReceber = soma(receberAberto);
  const aPagar   = soma(pagarAberto);
  const ordV = (a,b) => new Date(a.dataVencimento) - new Date(b.dataVencimento);
  const atrasoRec = entradas.filter(l => aberto(l) && venc(l)).sort(ordV);
  const atrasoPag = saidas.filter(l => aberto(l) && venc(l)).sort(ordV);
  const totAtrasoRec = soma(atrasoRec);
  const totAtrasoPag = soma(atrasoPag);
  const recebidoTot = soma(entradas.filter(l => l.status === "pago"));
  const pagoTot = soma(saidas.filter(l => l.status === "pago"));
  const saldo = saldoInicial + recebidoTot - pagoTot;
  const em7 = (l) => { if(!l.dataVencimento) return false; const d = new Date(l.dataVencimento); d.setHours(0,0,0,0); const diff = (d - hoje) / 86400000; return diff >= 0 && diff <= 7; };
  const proxRec = entradas.filter(l => aberto(l) && em7(l)).sort(ordV);
  const proxPag = saidas.filter(l => aberto(l) && em7(l)).sort(ordV);

  const WON = ["aprovado","recusado","expirado"];
  const orcVendaMes = orc.filter(o => WON.includes(o.status) && noPeriodo(o.dataAprovacao));
  const negGanhoMes = leads.filter(l => l.stage === "ganho" && noPeriodo(l.dataFechamento));
  const pipeOrcArr = orc.filter(o => ["rascunho","enviado"].includes(o.status));
  const pipeNegArr = leads.filter(l => !["ganho","perdido"].includes(l.stage));
  const vendaOrcMes = somaT(orcVendaMes);
  const vendaNegMes = negGanhoMes.reduce((a,l) => a + Number(l.valor||0), 0);
  const vendasMes = vendaOrcMes + vendaNegMes;
  const pipeOrc = somaT(pipeOrcArr);
  const pipeNeg = pipeNegArr.reduce((a,l) => a + Number(l.valor||0), 0);
  const pipeline = pipeOrc + pipeNeg;

  const finData = (arr) => ({ cols:["Descrição","Cliente","Categoria","Emissão","Vencimento","Pagamento","Status"], rows: arr.map(l => ({ cells:[l.descricao||"—", l.clienteNome||"—", l.categoria||"—", fmtD(l.dataEmissao), fmtD(l.dataVencimento), fmtD(l.dataPagamento), l.status||"—"], valor:l.valor })) });
  const orcData = (arr) => ({ cols:["Número","Cliente","Assunto","Vendedor","Emissão","Aprovação","Status"], rows: arr.map(o => ({ cells:[o.numero||"—", o.nomeCliente||"—", o.titulo||"—", o.nomeResponsavel||"—", fmtD(o.dataEmissao), fmtD(o.dataAprovacao), STLBL[o.status]||o.status||"—"], valor:o.total })) });
  const leadData = (arr) => ({ cols:["Cliente","Serviço","Vendedor","Etapa"], rows: arr.map(l => ({ cells:[l.nomeCliente||l.nome||"—", l.servico||"—", l.nomeResponsavel||"—", l.etapaNegociacao||l.stage||"—"], valor:l.valor })) });
  const mixData = (orcArr, negArr) => ({ cols:["Tipo","Cliente","Descrição","Responsável","Data"], rows:[
    ...orcArr.map(o => ({ cells:["Orçamento", o.nomeCliente||"—", (o.numero?o.numero+" · ":"")+(o.titulo||""), o.nomeResponsavel||"—", fmtD(o.dataAprovacao||o.dataEmissao)], valor:o.total })),
    ...negArr.map(l => ({ cells:["Negociação", l.nomeCliente||l.nome||"—", l.servico||"—", l.nomeResponsavel||"—", fmtD(l.dataFechamento)], valor:l.valor })),
  ] });
  const abrir = (titulo, data) => setDetalhe({ titulo, ...data });

  const perLabel = modo === "ano" ? String(mesRef.ano) : new Date(mesRef.ano, mesRef.mes, 1).toLocaleDateString("pt-BR", { month:"long", year:"numeric" });
  const pill = (on) => ({ padding:"6px 14px", fontSize:12.5, fontWeight:600, borderRadius:8, cursor:"pointer", border:"1px solid var(--border-color)", background: on ? "#EE6A1A" : "var(--card-bg)", color: on ? "#fff" : "var(--text-secondary)" });
  const arrowBtn = { background:"none", border:"none", cursor:"pointer", fontSize:22, color:"#EE6A1A", lineHeight:1, padding:"0 4px" };
  const passo = (delta) => { if (modo === "ano") { setMesRef(r => ({ ...r, ano: r.ano + delta })); } else { const m = mesRef.mes + delta; setMesRef(m < 0 ? { ano: mesRef.ano-1, mes:11 } : m > 11 ? { ano: mesRef.ano+1, mes:0 } : { ano: mesRef.ano, mes:m }); } };

  const Card = ({ label, valor, cor, Icon, sub, destaque, onClick }) => (
    <div onClick={onClick} style={{ flex:1, minWidth:180, background: destaque ? cor+"12" : "var(--card-bg)", border: "1px solid " + (destaque ? cor+"55" : "var(--border-color)"), borderRadius:12, padding:"16px 18px", cursor: onClick ? "pointer" : "default", transition:"transform .1s" }}
      onMouseEnter={e => { if (onClick) e.currentTarget.style.transform = "translateY(-2px)"; }}
      onMouseLeave={e => { e.currentTarget.style.transform = "none"; }}>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start" }}>
        <div style={{ fontSize:12, color:"var(--text-secondary)", fontWeight:600, textTransform:"uppercase", letterSpacing:.4 }}>{label}</div>
        {Icon && <Icon size={18} color={cor} />}
      </div>
      <div style={{ fontSize:24, fontWeight:800, color:cor, marginTop:8 }}>{valor}</div>
      {sub && <div style={{ fontSize:11, color:"var(--text-muted)", marginTop:3 }}>{sub}</div>}
    </div>
  );

  const ListaVenc = ({ titulo, arr, cor, atraso }) => (
    <div style={{ flex:1, minWidth:300, background:"var(--card-bg)", border:"1px solid var(--border-color)", borderRadius:12, overflow:"hidden" }}>
      <div style={{ padding:"12px 16px", borderBottom:"1px solid var(--border-color)", display:"flex", justifyContent:"space-between", alignItems:"center" }}>
        <span style={{ fontSize:13, fontWeight:700, color:"var(--text-primary)" }}>{titulo}</span>
        <span style={{ fontSize:13, fontWeight:800, color:cor }}>{fmt(soma(arr))}</span>
      </div>
      {!arr.length && <div style={{ padding:18, fontSize:13, color:"var(--text-muted)", textAlign:"center" }}>Nada aqui.</div>}
      {arr.slice(0,8).map((l,i) => (
        <div key={i} onClick={() => abrir(titulo, finData([l]))} style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"9px 16px", borderBottom:"1px solid var(--border-light)", gap:10, cursor:"pointer" }}>
          <div style={{ minWidth:0 }}>
            <div style={{ fontSize:13, color:"var(--text-primary)", fontWeight:600, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", maxWidth:220 }}>{l.descricao || "—"}</div>
            <div style={{ fontSize:11, color:"var(--text-muted)" }}>{l.clienteNome || "—"} · venc. {fmtD(l.dataVencimento)}{atraso ? " · " + dias(l.dataVencimento) + "d atraso" : ""}</div>
          </div>
          <span style={{ fontSize:13, fontWeight:700, color:cor, whiteSpace:"nowrap" }}>{fmt(l.valor)}</span>
        </div>
      ))}
      {arr.length > 8 && <div onClick={() => abrir(titulo, finData(arr))} style={{ padding:"8px 16px", fontSize:11, color:"#579bfc", textAlign:"center", cursor:"pointer", fontWeight:600 }}>+{arr.length - 8} outros — ver todos</div>}
    </div>
  );

  const Titulo = ({ children }) => <div style={{ fontSize:13, fontWeight:700, color:"var(--text-secondary)", textTransform:"uppercase", letterSpacing:.4, margin:"8px 0 10px" }}>{children}</div>;

  if (load) return <div style={{ padding:40, textAlign:"center", color:"var(--text-muted)" }}>Carregando painel...</div>;

  const th = { padding:"9px 12px", textAlign:"left", fontSize:11, fontWeight:700, color:"var(--text-muted)", textTransform:"uppercase", letterSpacing:.3, borderBottom:"1px solid var(--border-color)", whiteSpace:"nowrap", position:"sticky", top:0, background:"var(--card-bg)" };
  const tdc = { padding:"9px 12px", fontSize:12.5, borderBottom:"1px solid var(--border-light)", whiteSpace:"nowrap" };

  return (
    <div style={{ padding:"24px 28px", background:"var(--page-bg)", minHeight:"100vh" }}>
      <div style={{ marginBottom:14 }}>
        <h1 style={{ fontSize:24, fontWeight:800, color:"var(--text-primary)", margin:0 }}>Painel de Gestão</h1>
        <p style={{ fontSize:13, color:"var(--text-secondary)", margin:"4px 0 0" }}>A Receber, A Pagar e Vendas do período — {perLabel} · Saldo, atrasos e pipeline são ao vivo · clique num card pra detalhar</p>
      </div>

      <div style={{ display:"flex", gap:10, alignItems:"center", flexWrap:"wrap", marginBottom:16 }}>
        <button onClick={() => setModo("mes")} style={pill(modo==="mes")}>Mensal</button>
        <button onClick={() => setModo("ano")} style={pill(modo==="ano")}>Anual</button>
        <div style={{ display:"flex", alignItems:"center", gap:6 }}>
          <button onClick={() => passo(-1)} style={arrowBtn}>‹</button>
          <span style={{ fontSize:14, fontWeight:800, color:"var(--text-primary)", textTransform:"capitalize", minWidth: modo==="ano"?60:140, textAlign:"center" }}>{perLabel}</span>
          <button onClick={() => passo(1)} style={arrowBtn}>›</button>
        </div>
        <button onClick={() => { const d = new Date(); setMesRef({ ano:d.getFullYear(), mes:d.getMonth() }); setModo("mes"); }} style={pill(false)}>Hoje</button>
      </div>

      {(atrasoRec.length + atrasoPag.length) > 0 && (
        <div onClick={() => abrir("Em atraso (ao vivo)", finData([...atrasoRec, ...atrasoPag]))} style={{ display:"flex", alignItems:"center", gap:10, background:"#E24B4A14", border:"1px solid #E24B4A44", borderRadius:10, padding:"12px 16px", marginBottom:16, color:"#E24B4A", fontSize:13, fontWeight:600, cursor:"pointer" }}>
          <AlertTriangle size={18} /> {atrasoRec.length + atrasoPag.length} lançamento(s) em atraso — a receber {fmt(totAtrasoRec)} · a pagar {fmt(totAtrasoPag)}
        </div>
      )}

      <div style={{ display:"flex", gap:12, flexWrap:"wrap", marginBottom:16 }}>
        <Card label="Saldo estimado" valor={fmt(saldo)} cor={saldo>=0?"#1D9E75":"#E24B4A"} Icon={Wallet} sub={"ao vivo · base " + fmt(saldoInicial)} onClick={() => abrir("Saldo estimado", { cols:["Componente"], rows:[{cells:["Recebido (entradas pagas)"], valor:recebidoTot},{cells:["Pago (saídas pagas)"], valor:-pagoTot},{cells:["Saldo base (Financeiro)"], valor:saldoInicial}] })} />
        <Card label="A Receber (período)" valor={fmt(aReceber)} cor="#1D9E75" Icon={ArrowDownCircle} sub={receberAberto.length + " lançamentos"} onClick={() => abrir("A Receber — " + perLabel, finData(receberAberto))} />
        <Card label="A Pagar (período)" valor={fmt(aPagar)} cor="#E24B4A" Icon={ArrowUpCircle} sub={pagarAberto.length + " lançamentos"} onClick={() => abrir("A Pagar — " + perLabel, finData(pagarAberto))} />
        <Card label="Em atraso (ao vivo)" valor={fmt(totAtrasoRec + totAtrasoPag)} cor="#EE6A1A" Icon={AlertTriangle} destaque sub={(atrasoRec.length + atrasoPag.length) + " vencidos"} onClick={() => abrir("Em atraso (ao vivo)", finData([...atrasoRec, ...atrasoPag]))} />
        <Card label="Vendas do período" valor={fmt(vendasMes)} cor="#1D9E75" Icon={TrendingUp} sub="aprovados + ganhos" onClick={() => abrir("Vendas — " + perLabel, mixData(orcVendaMes, negGanhoMes))} />
        <Card label="Pipeline (ao vivo)" valor={fmt(pipeline)} cor="#579bfc" Icon={Clock} sub="orçamentos + negociações" onClick={() => abrir("Pipeline em aberto", mixData(pipeOrcArr, pipeNegArr))} />
      </div>

      <Titulo>Em atraso (ao vivo)</Titulo>
      <div style={{ display:"flex", gap:12, flexWrap:"wrap", marginBottom:20 }}>
        <ListaVenc titulo="A Receber vencido" arr={atrasoRec} cor="#E24B4A" atraso />
        <ListaVenc titulo="A Pagar vencido" arr={atrasoPag} cor="#E24B4A" atraso />
      </div>

      <Titulo>Próximos 7 dias (ao vivo)</Titulo>
      <div style={{ display:"flex", gap:12, flexWrap:"wrap", marginBottom:20 }}>
        <ListaVenc titulo="A Receber (7 dias)" arr={proxRec} cor="#1D9E75" />
        <ListaVenc titulo="A Pagar (7 dias)" arr={proxPag} cor="#E24B4A" />
      </div>

      <Titulo>Vendas do período — {perLabel}</Titulo>
      <div style={{ display:"flex", gap:12, flexWrap:"wrap" }}>
        <Card label="Vendas (orçamentos)" valor={fmt(vendaOrcMes)} cor="#1D9E75" Icon={TrendingUp} onClick={() => abrir("Vendas — Orçamentos — " + perLabel, orcData(orcVendaMes))} />
        <Card label="Vendas (negociações)" valor={fmt(vendaNegMes)} cor="#1D9E75" Icon={TrendingUp} onClick={() => abrir("Vendas — Negociações — " + perLabel, leadData(negGanhoMes))} />
        <Card label="Pipeline orçamentos" valor={fmt(pipeOrc)} cor="#579bfc" sub="ao vivo · rascunho + enviado" onClick={() => abrir("Pipeline — Orçamentos", orcData(pipeOrcArr))} />
        <Card label="Pipeline negociações" valor={fmt(pipeNeg)} cor="#579bfc" sub="ao vivo · em aberto" onClick={() => abrir("Pipeline — Negociações", leadData(pipeNegArr))} />
      </div>

      {detalhe && (
        <div onClick={() => setDetalhe(null)} style={{ position:"fixed", inset:0, background:"rgba(0,0,0,.45)", display:"flex", alignItems:"flex-start", justifyContent:"center", padding:"28px 16px", zIndex:1000 }}>
          <div onClick={e => e.stopPropagation()} style={{ background:"var(--card-bg)", borderRadius:14, width:"100%", maxWidth:1240, maxHeight:"86vh", overflow:"auto", border:"1px solid var(--border-color)" }}>
            <div style={{ padding:"16px 20px", borderBottom:"1px solid var(--border-color)", display:"flex", justifyContent:"space-between", alignItems:"center", position:"sticky", top:0, background:"var(--card-bg)", zIndex:2 }}>
              <div>
                <div style={{ fontSize:16, fontWeight:800, color:"var(--text-primary)" }}>{detalhe.titulo}</div>
                <div style={{ fontSize:12, color:"var(--text-muted)" }}>{detalhe.rows.length} item(ns) · total {fmt(detalhe.rows.reduce((a,x) => a + Number(x.valor||0), 0))}</div>
              </div>
              <button onClick={() => setDetalhe(null)} style={{ background:"none", border:"none", cursor:"pointer", fontSize:22, color:"var(--text-muted)", lineHeight:1 }}>×</button>
            </div>
            {!detalhe.rows.length ? (
              <div style={{ padding:30, textAlign:"center", color:"var(--text-muted)" }}>Nenhum item.</div>
            ) : (
              <div style={{ overflowX:"auto" }}>
                <table style={{ width:"100%", borderCollapse:"collapse" }}>
                  <thead>
                    <tr>
                      {detalhe.cols.map((c,i) => <th key={i} style={th}>{c}</th>)}
                      <th style={{ ...th, textAlign:"right" }}>Valor</th>
                    </tr>
                  </thead>
                  <tbody>
                    {detalhe.rows.map((r,i) => (
                      <tr key={i}>
                        {r.cells.map((c,j) => <td key={j} title={String(c)} style={{ ...tdc, color: j===0 ? "var(--text-primary)" : "var(--text-secondary)", fontWeight: j===0 ? 600 : 400, maxWidth: j===0 ? 260 : 150, overflow:"hidden", textOverflow:"ellipsis" }}>{c}</td>)}
                        <td style={{ ...tdc, textAlign:"right", fontWeight:700, color: Number(r.valor) < 0 ? "#E24B4A" : "var(--text-primary)" }}>{fmt(r.valor)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
